import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Building2, Loader2 } from 'lucide-react';
import { useAdminPortPros, type PortPro } from '@/hooks/usePortPros';
import { PortProFormDialog } from './PortProFormDialog';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

const usePortNames = (ids: string[]) => {
  return useQuery({
    queryKey: ['port-names', ids.sort().join(',')],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from('ports' as any)
        .select('id,nom,ville')
        .in('id', ids);
      const map: Record<string, { nom: string; ville: string | null }> = {};
      (data || []).forEach((p: any) => { map[p.id] = { nom: p.nom, ville: p.ville }; });
      return map;
    },
  });
};

export const AdminPortPros = () => {
  const { data: pros = [], isLoading, create, update, remove } = useAdminPortPros();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PortPro | null>(null);
  const [search, setSearch] = useState('');

  const portIds = useMemo(() => Array.from(new Set(pros.map((p) => p.port_id))), [pros]);
  const { data: portsMap = {} } = usePortNames(portIds);

  const filtered = pros.filter((p) => {
    const s = search.toLowerCase();
    if (!s) return true;
    const portInfo = portsMap[p.port_id];
    return (
      p.nom_entreprise.toLowerCase().includes(s) ||
      p.specialite.toLowerCase().includes(s) ||
      (p.ville || '').toLowerCase().includes(s) ||
      (portInfo?.nom || '').toLowerCase().includes(s)
    );
  });

  const handleSubmit = async (data: any) => {
    if (editing) await update.mutateAsync({ id: editing.id, ...data });
    else await create.mutateAsync(data);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          Professionnels des ports
          <Badge variant="secondary">{pros.length}</Badge>
        </CardTitle>
        <Button onClick={() => { setEditing(null); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" /> Ajouter
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          placeholder="Rechercher (nom, port, ville, spécialité…)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
        />

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-10">Aucun professionnel.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Logo</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Spécialité</TableHead>
                  <TableHead>Port</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => {
                  const portInfo = portsMap[p.port_id];
                  return (
                    <TableRow key={p.id}>
                      <TableCell>
                        {p.logo_url ? (
                          <img src={p.logo_url} alt="" className="h-10 w-20 rounded object-cover bg-muted" />
                        ) : (
                          <div className="h-10 w-20 rounded bg-muted flex items-center justify-center">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{p.nom_entreprise}</TableCell>
                      <TableCell><Badge variant="secondary">{p.specialite}</Badge></TableCell>
                      <TableCell className="text-sm">
                        {portInfo ? `${portInfo.nom}${portInfo.ville ? ` — ${portInfo.ville}` : ''}` : '—'}
                      </TableCell>
                      <TableCell className="text-sm">{p.telephone || '—'}</TableCell>
                      <TableCell>
                        <Badge variant={p.actif ? 'default' : 'outline'}>
                          {p.actif ? 'Actif' : 'Inactif'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setOpen(true); }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Supprimer ce professionnel ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Cette action est irréversible : « {p.nom_entreprise} » sera supprimé.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction onClick={() => remove.mutate(p.id)}>
                                  Supprimer
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <PortProFormDialog
          open={open}
          onOpenChange={setOpen}
          pro={editing}
          onSubmit={handleSubmit}
        />
      </CardContent>
    </Card>
  );
};

export default AdminPortPros;
